/**
 * History — one merged, reverse-chronological feed of everything that happened.
 *
 * `openapi` puts the case: it "exists so a user never has to reconcile four
 * different endpoints by hand to answer 'what happened to my money'". Until
 * §F28 the endpoint behind it was the static spec example, so this table would
 * have rendered a fixture perfectly (§F27h).
 *
 * **The market cell is a deep link, and that link is the point.** Phase 8's
 * fifth exit criterion is that it opens the settled market and renders from
 * cache on the second visit. Nothing here implements the caching: the origin
 * serves a settled market `immutable` once `resolution.corrections_closed` is
 * true, and `api/http/cache.ts` parses what it is told. This table's job is to
 * carry the anchor so the link lands on the right minute out of 2,880.
 *
 * **The `FEE` rows read `0.000000` and are shown anyway.** The fee rate is
 * `0.0000` at launch and comes from `/config`; a client that hid zero-valued fee
 * rows would teach the user that fees do not appear, and the day the rate moves
 * nothing here would have to change.
 */

import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState, Pill, Table } from '@/components/primitives'
import type { TableColumn } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatSignedUsdc, formatUtcTime } from '@/lib/format'
import { isMarketId, parseMarketId } from '@/lib/market-id'

export interface ActivityRow extends Record<string, unknown> {
  event_id?: string
  type?: string
  title?: string
  detail?: string | null
  amount?: string
  market_id?: string | null
  created_at_ms?: number
}

/** Which rows read as money arriving. Everything else is neutral or spent. */
const TONE: Record<string, 'up' | 'down' | 'neutral' | 'reward'> = {
  SETTLEMENT: 'up',
  FILL: 'neutral',
  FEE: 'neutral',
  REBATE: 'reward',
  REWARD: 'reward',
  REFERRAL: 'reward',
  ADJUSTMENT: 'neutral',
}

export const ActivityTable = memo(function ActivityTable({
  rows,
  className,
}: {
  rows: readonly ActivityRow[]
  className?: string
}) {
  const columns = useMemo<TableColumn<ActivityRow>[]>(
    () => [
      // **Only `What` is elastic.** The other four columns carry values whose
      // width is a fact — a `hh:mm:ss`, a pill, a `market_id`, a signed amount —
      // so a `1fr` track on any of them takes a quarter of the row to render
      // eight characters and pays for it out of the one column whose content is
      // a sentence. `What` is the only cell that can use the slack, so it gets
      // all of it. `What` keeps a floor rather than a bare `1fr`, because below
      // ~640px the four fixed tracks are wider than the viewport and a `1fr`
      // remainder resolves to zero — the table scrolls instead of rendering an
      // empty column.
      {
        id: 'time',
        header: 'When',
        width: 'max-content',
        cell: (row) => (typeof row.created_at_ms === 'number' ? formatUtcTime(row.created_at_ms) : '—'),
      },
      {
        id: 'type',
        header: 'Type',
        card: 'badge',
        width: 'max-content',
        cell: (row) => <Pill tone={TONE[String(row.type)] ?? 'neutral'}>{String(row.type ?? '—')}</Pill>,
      },
      {
        id: 'what',
        header: 'What',
        card: 'title',
        width: 'minmax(calc(224 * var(--ud-u)), 1fr)',
        cell: (row) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-body-sm text-text-primary" title={row.title ?? undefined}>
              {row.title ?? '—'}
            </span>
            {row.detail ? (
              <span className="truncate text-caption text-text-tertiary" title={row.detail}>
                {row.detail}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'market',
        header: 'Market',
        width: 'max-content',
        cell: (row) => <MarketLink marketId={row.market_id ?? null} />,
      },
      {
        id: 'amount',
        header: 'Amount',
        numeric: true,
        cell: (row) => {
          const amount = typeof row.amount === 'string' ? row.amount : null
          if (amount === null) return '—'
          const negative = amount.trimStart().startsWith('-')
          // Zero is neither. A `0.000000` fee row rendered in the win colour
          // would be the only green nothing on the screen.
          const zero = !/[1-9]/.test(amount)
          return (
            <span className={zero ? 'text-text-tertiary' : negative ? 'text-down-text' : 'text-up-text'}>
              {formatSignedUsdc(amount)}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <Table
      label="activity"
      {...(className ? { className } : {})}
      density="comfortable"
      columns={columns}
      rows={rows as ActivityRow[]}
      rowKey={(row) => String(row.event_id)}
      empty={<EmptyState>Nothing in the last 7 days. Older results are under Settled.</EmptyState>}
    />
  )
})

/**
 * The deep link. `/trade/{asset}?anchor=` is the canonical form (`router.tsx`):
 * the anchor is a query parameter so that switching assets is a parameter change
 * rather than a navigation, and so a link to "the BTC market at 21:46" is
 * reproducible out of 2,880 a day.
 */
function MarketLink({ marketId }: { marketId: string | null }) {
  if (marketId === null || !isMarketId(marketId)) return <span className="text-text-tertiary">—</span>
  const { asset, anchorS } = parseMarketId(marketId)
  return (
    <Link
      to={`/trade/${asset}?anchor=${anchorS}`}
      data-market-id={marketId}
      title={marketId}
      className={cx(
        'truncate text-body-sm text-text-link',
        'focus-visible:shadow-focus focus-visible:outline-none hover:underline',
      )}
    >
      {marketId}
    </Link>
  )
}
